import { useEffect, useState } from "react";
import { BarChart3, Trophy, Cloud, Star, Sparkles } from "lucide-react";

const BASE_TYPES = [
  {
    id: "live-bar-poll",
    type: "Live Bar Poll",
    tag: "Instant Consensus",
    icon: BarChart3,
    gradient: "from-indigo-500 to-blue-600",
    question: "Which feature should our engineering team prioritize for Q3?",
    optionLabels: ["AI Automated Slide Generation", "Custom Domain Join Links", "Native Zoom/Teams Embed"],
  },
  {
    id: "quiz-competition",
    type: "Quiz & Competition",
    tag: "Timed Speed Scoring",
    icon: Trophy,
    gradient: "from-amber-400 to-orange-500",
    question: "What is the default port for local FastAPI server backends?",
    quiz: { correct: "8000 (FastAPI default)", leader: "#1 Alex M. — 4,820 pts" },
  },
  {
    id: "dynamic-word-cloud",
    type: "Dynamic Word Cloud",
    tag: "Organic Free-Text",
    icon: Cloud,
    gradient: "from-emerald-400 to-teal-600",
    question: "In one word, how would you describe our new company culture?",
    words: ["INNOVATIVE", "Transparent", "Fast-Paced", "Collaborative", "Empowering"],
  },
  {
    id: "rating-survey",
    type: "Rating Survey",
    tag: "Star Feedback & AI Insights",
    icon: Star,
    gradient: "from-rose-500 to-indigo-600",
    question: "Rate this week's clinical rounds and shift handover process",
    survey: { insight: "92% of Northshore Medical staff praised handover clarity." },
  },
];

const WORD_SIZE_CLASSES = [
  "text-2xl font-black text-[#4F46E5]",
  "text-lg font-bold text-emerald-600",
  "text-base font-semibold text-slate-600",
  "text-xl font-bold text-[#FF5733]",
  "text-sm font-medium text-slate-400",
];

function useTicker(intervalMs = 1300) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return tick;
}

export function QuizSurveyShowcase() {
  const tick = useTicker();

  const shift = Math.round(6 * Math.sin(tick * 0.5));
  const pct1 = 68 + shift;
  const pct2 = Math.round((100 - pct1) * 0.65);
  const pct3 = 100 - pct1 - pct2;
  const timeLeft = String(Math.max(15 - (tick % 16), 0)).padStart(2, "0") + "s";
  const wordOrder = [...BASE_TYPES[2].words];
  for (let i = 0; i < tick % wordOrder.length; i++) wordOrder.push(wordOrder.shift());
  const surveyAvg = (4.5 + 0.4 * ((tick % 5) / 4)).toFixed(1);

  const TYPES = BASE_TYPES.map((t) => {
    if (t.id === "live-bar-poll") {
      return { ...t, options: [
        { label: t.optionLabels[0], pct: pct1, bar: "bg-gradient-to-r from-[#4F46E5] to-[#FF5733]" },
        { label: t.optionLabels[1], pct: pct2, bar: "bg-slate-300" },
        { label: t.optionLabels[2], pct: pct3, bar: "bg-slate-300" },
      ] };
    }
    if (t.id === "quiz-competition") {
      return { ...t, quiz: { ...t.quiz, timeLeft, bonus: `${(1 + (tick % 5) * 0.1).toFixed(1)}x Speed Bonus Active` } };
    }
    if (t.id === "dynamic-word-cloud") {
      return { ...t, words: wordOrder.map((word, i) => ({ word, cls: WORD_SIZE_CLASSES[i] })) };
    }
    if (t.id === "rating-survey") {
      return { ...t, survey: { ...t.survey, average: `${surveyAvg} / 5.0` } };
    }
    return t;
  });

  return (
    <section className="px-6 max-w-7xl mx-auto pb-28" data-testid="section-quiz-survey-types">
      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4F46E5] bg-[#4F46E5]/10 px-3 py-1.5 rounded-full mb-4">
        <Sparkles className="w-3.5 h-3.5" /> Interactive Slide Catalog
      </span>
      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">Every quiz &amp; survey format, built in</h2>
      <p className="text-slate-600 max-w-2xl mb-12">Four specialized interactive formats designed for maximum room engagement and real-time clarity.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {TYPES.map((t) => (
          <div key={t.id} className="relative" data-testid={`type-card-${t.id}`}>
            <div className={`absolute -inset-1 bg-gradient-to-r ${t.gradient} opacity-10 rounded-3xl blur-2xl -z-10 pointer-events-none`} />
            <div className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_20px_50px_rgba(15,23,42,0.06)] hover:shadow-[0_25px_60px_rgba(79,70,229,0.12)] transition-all duration-300 p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${t.gradient}`}>
                  <t.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.tag}</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-1">{t.type}</h3>
              <p className="text-sm text-slate-500 mb-5">{t.question}</p>

              {t.options && (
                <div className="space-y-2.5 mt-auto">
                  {t.options.map((o) => (
                    <div key={o.label}>
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1" style={{ fontFamily: "Arial, sans-serif" }}>
                        <span>{o.label}</span>
                        <span className="font-semibold text-slate-700">{o.pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${o.bar} transition-all duration-700 ease-in-out`} style={{ width: `${o.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {t.quiz && (
                <div className="mt-auto space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-2xl font-black text-amber-500">{t.quiz.timeLeft}</span>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">{t.quiz.bonus}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">✓ {t.quiz.correct}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-amber-400" /> {t.quiz.leader}</div>
                </div>
              )}

              {t.words && (
                <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
                  {t.words.map((w) => (
                    <span key={w.word} className={w.cls}>{w.word}</span>
                  ))}
                </div>
              )}

              {t.survey && (
                <div className="mt-auto space-y-3">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />)}
                    <span className="font-bold text-slate-700 text-sm">{t.survey.average}</span>
                  </div>
                  <div className="flex items-start gap-1.5 text-xs text-slate-500 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2.5">
                    <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#4F46E5]" /> AI Insight: {t.survey.insight}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
